import type { ProcedureColumn } from "../../../models/audit/auditSession";
import type {
  CollabDraftAttributeTestColumn as DraftAttributeTestColumn,
  CollabDraftSupportColumn as DraftSupportColumn,
  CollabProposedAttribute as ProposedAttribute,
} from "../../../models/audit/collabPlanTypes";
import type { ColumnVersionSnapshot } from "../../../hooks/useColumnHistory";

/**
 * Bridges the revise wizard's draft-plan columns and the persisted
 * column-history world (`ProcedureColumn` snapshots). Two directions:
 *
 * - draft → `ProcedureColumn`: synthesize a wrapper-shaped "current" so the
 *   shared `buildColumnVersionDiff` / `ColumnHistoryView` can diff a history
 *   snapshot against the live draft.
 * - snapshot → draft changes: turn a restored snapshot into the field changes
 *   the caller applies to that one column.
 *
 * Entity-agnostic: it works on columns, which do not vary per entity, so
 * nothing here is generic in the way the table editor is.
 */

/** The wizard's draft-plan attributes are keyed by reference; history
 * snapshots link attributes by row id. Resolve in both directions. */
const attributeIdByReference = (
  proposedAttributes: ProposedAttribute[],
  reference: string | null | undefined,
): string | null =>
  proposedAttributes.find((a) => a.reference === reference)?.id ?? null;

const attributeReferenceById = (
  proposedAttributes: ProposedAttribute[],
  attributeId: string | null | undefined,
): string | null =>
  proposedAttributes.find((a) => a.id === attributeId)?.reference ?? null;

/** "{reference} - {statement}" label for the diff's Assigned Attribute row,
 * consistent with the workpaper editor's dropdown labels. */
export const makeResolveAttributeName =
  (proposedAttributes: ProposedAttribute[]) =>
  (attributeId: string): string => {
    const attribute = proposedAttributes.find((a) => a.id === attributeId);
    if (!attribute) return `Deleted attribute (${attributeId})`;
    return `${attribute.reference} - ${attribute.statement}`;
  };

export const draftAttrTestToProcedureColumn = (
  column: DraftAttributeTestColumn,
  proposedAttributes: ProposedAttribute[],
): ProcedureColumn => {
  const attributeId = attributeIdByReference(
    proposedAttributes,
    column.attributeReference,
  );
  return {
    id: column.id ?? "draft",
    columnType: "attribute_test",
    columnOrder: 0,
    columnVersion: 0,
    attributeId,
    supportColumn: null,
    evaluatedColumn: null,
    attributeTestColumn: {
      id: column.id ?? "draft",
      name: column.name ?? null,
      passCriteria: column.passCriteria,
      failCriteria: column.failCriteria ?? null,
      naCriteria: column.naCriteria ?? null,
      attributeId,
      procedureGuidance: column.procedureGuidance ?? null,
      tolerances: column.tolerances ?? null,
      expectedEvidences: column.expectedEvidences ?? null,
      createdAt: "",
      updatedAt: "",
    },
    createdAt: "",
    updatedAt: "",
  };
};

export const draftSupportToProcedureColumn = (
  column: DraftSupportColumn,
): ProcedureColumn => ({
  id: column.id ?? "draft",
  columnType: "support",
  columnOrder: 0,
  columnVersion: 0,
  attributeId: null,
  supportColumn: {
    id: column.id ?? "draft",
    name: column.name,
    description: column.description,
    dataType: column.dataType,
    procedureRequiredDocuments: [],
    createdAt: "",
    updatedAt: "",
  },
  evaluatedColumn: null,
  attributeTestColumn: null,
  createdAt: "",
  updatedAt: "",
});

export interface AttrTestRestoreResult {
  changes: Partial<DraftAttributeTestColumn>;
  /** The snapshot's attribute id when it no longer exists in the draft plan —
   * the restore kept the current assignment because there is nothing to point
   * at. Callers surface this so the restore doesn't silently diverge from the
   * previewed diff. Null when the attribute restored cleanly (or the snapshot
   * had none). */
  unrestoredAttributeId: string | null;
}

/**
 * Field changes to apply a restored snapshot onto a draft attribute-test
 * column, or null when the snapshot has no attribute-test definition (a
 * wrapper whose type changed — nothing sensible to restore onto this row).
 *
 * The linked attribute follows the snapshot: a snapshot with no attribute
 * clears the draft's reference (matching the previewed diff), and a snapshot
 * whose attribute still exists in the draft plan restores it. Only when the
 * snapshot's attribute has since been deleted from the plan is the current
 * reference kept — reported via `unrestoredAttributeId`.
 */
export const snapshotToAttrTestChanges = (
  snapshot: ColumnVersionSnapshot,
  proposedAttributes: ProposedAttribute[],
): AttrTestRestoreResult | null => {
  const restored = snapshot.snapshot.attributeTestColumn;
  if (!restored) return null;

  const changes: Partial<DraftAttributeTestColumn> = {
    name: restored.name ?? null,
    passCriteria: restored.passCriteria,
    failCriteria: restored.failCriteria,
    naCriteria: restored.naCriteria,
    procedureGuidance: restored.procedureGuidance,
    tolerances: restored.tolerances,
    expectedEvidences: restored.expectedEvidences,
  };

  const snapshotAttributeId =
    snapshot.snapshot.attributeId ?? restored.attributeId ?? null;
  if (snapshotAttributeId === null) {
    changes.attributeReference = null;
    return { changes, unrestoredAttributeId: null };
  }

  const restoredReference = attributeReferenceById(
    proposedAttributes,
    snapshotAttributeId,
  );
  if (restoredReference !== null) {
    changes.attributeReference = restoredReference;
    return { changes, unrestoredAttributeId: null };
  }

  return { changes, unrestoredAttributeId: snapshotAttributeId };
};

/** Support-column counterpart of `snapshotToAttrTestChanges`. */
export const snapshotToSupportChanges = (
  snapshot: ColumnVersionSnapshot,
): Partial<DraftSupportColumn> | null => {
  const restored = snapshot.snapshot.supportColumn;
  if (!restored) return null;

  return {
    name: restored.name,
    description: restored.description,
    dataType: restored.dataType === "number" ? "number" : "string",
  };
};
